import {createSmartContext} from '@packages/utilities/smart-context';

/**
 * Accent color applied to a frame's UI.
 */
export type FrameAccent = 'green' | 'blue';

export type FrameChromeState = {
  accent: FrameAccent;
  isActive: boolean;
  isSelected: boolean;
};

const DEFAULT_FRAME_CHROME_STATE: FrameChromeState = {
  accent: 'blue',
  isActive: false,
  isSelected: false,
};

/**
 * The individual building blocks that make up a frame (chrome, tools, overlays, etc.)
 * need to render in a consistent accent color and read the frame's active/selected
 * state without each consumer having to thread the values through props. This
 * context lets `Frame` publish accent and selection once so any descendant piece of
 * frame UI can read exactly the field(s) it needs.
 */
const {
  ContextProvider: FrameChromeContextProvider,
  useContext: useFrameChromeContext,
} = createSmartContext<FrameChromeState>(DEFAULT_FRAME_CHROME_STATE);

export {FrameChromeContextProvider};

const getAccent = (state: FrameChromeState) => state.accent;
const getIsActive = (state: FrameChromeState) => state.isActive;
const getIsSelected = (state: FrameChromeState) => state.isSelected;

export const useFrameAccent = (): FrameAccent =>
  useFrameChromeContext(getAccent);

export const useFrameIsActive = (): boolean =>
  useFrameChromeContext(getIsActive);

export const useFrameIsSelected = (): boolean =>
  useFrameChromeContext(getIsSelected);

/**
 * Composes the two selection fields for consumers that need both. Never combine
 * these into a single selector that returns a freshly-constructed object —
 * `useSyncExternalStore`'s `Object.is` check would see a new reference on every
 * read and defeat the point of subscribing per field.
 */
export const useFrameSelection = (): {
  isActive: boolean;
  isSelected: boolean;
} => {
  const isActive = useFrameIsActive();
  const isSelected = useFrameIsSelected();
  return {isActive, isSelected};
};
